import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Menu, X, Bookmark, Home, ShoppingBag, User } from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import UserMenu from "../user/UserMenu";
import SearchPopup from "./SearchPopup";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { user } = useSelector((s) => s.auth);
  const { cartItems } = useSelector((s) => s.cart);
  const location = useLocation();
  const isHome = location.pathname === "/";

  const navLinks = [
    { path: "/stories", label: "STORIES" },
    { path: "/supportUkraine", label: "ART FOR UKRAINE" },
    { path: "/blogs", label: "BLOG" },
    { path: "/about", label: "ABOUT" },
    { path: "/contact", label: "CONNECT" },
  ];

  const cartCount = useMemo(
    () =>
      cartItems?.length
        ? cartItems.reduce((a, i) => a + (i?.quantity ?? 0), 0)
        : 0,
    [cartItems]
  );

  const toggleMenu = useCallback(() => setMenuOpen((v) => !v), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  // Prevent background scroll under drawer (incl. iOS)
  useEffect(() => {
    const original = document.body.style.overflow;
    const prevent = (e) => e.preventDefault();
    if (menuOpen) {
      document.body.style.overflow = "hidden";
      document.addEventListener("touchmove", prevent, { passive: false });
    } else {
      document.body.style.overflow = original || "";
      document.removeEventListener("touchmove", prevent);
    }
    return () => {
      document.body.style.overflow = original || "";
      document.removeEventListener("touchmove", prevent);
    };
  }, [menuOpen]);

  // Close drawer on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search, location.hash]);

  // Esc to close drawer
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const iconColor = isHome ? "text-[#f5f3ea]" : "text-stone-700";

  return (
    <nav
      aria-label="Primary"
      className={`absolute top-0 left-0 w-full z-40 ${
        isHome ? "bg-transparent" : "bg-[#f5f3ea]/90 shadow-sm"
      }`}
    >
    
      <div className="flex items-center justify-between md:mt-8 p-6 md:p-0">
        <Link to="/" aria-label="Go to homepage" className="flex flex-col">
          <h2
            className={`${
              isHome ? "text-[#f5f3ea]" : "text-[#BC3F4A]"
            } text-2xl sm:text-2xl md:text-3xl md:pl-10 tracking-tight font-medium font-playfair`}
          >
            sanguine accent
          </h2>
          <span
            className={`${
              isHome ? "text-[#f5f3ea]/70" : "text-stone-400"
            } text-[10px] tracking-widest uppercase md:pl-10 font-sourcesans hidden md:block`}
          >
            Make it. Mean it.
          </span>
        </Link>

       
        <div className="flex items-center space-x-0">
          {/* Mobile hamburger */}
          <div className="flex md:hidden py-1 items-center">
            <button
              onClick={toggleMenu}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
            >
              {menuOpen ? (
                <X className={`${iconColor} h-7 w-7`} aria-hidden="true" />
              ) : (
                <Menu className={`${iconColor} h-7 w-7`} aria-hidden="true" />
              )}
            </button>
          </div>

          {/* Desktop icons */}
          <div className="hidden md:flex items-center space-x-0 pr-10">
            <UserMenu user={user} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
            <SearchPopup />
            <Link to="/wishlist" aria-label="Wishlist">
              <Bookmark
                strokeWidth={2}
                className={`${iconColor} h-6 w-6 mr-2 ml-2`}
                aria-hidden="true"
              />
            </Link>
            <Link
              to="/cart"
              className="relative flex items-center hover:text-[#BC3F4A] transition"
              aria-label={`Cart${cartCount > 0 ? `, ${cartCount} items` : ""}`}
            >
              <ShoppingBag
                strokeWidth={2}
                className={`${iconColor} h-6 w-6`}
                aria-hidden="true"
              />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#BC3F4A] text-[#f5f3ea] text-xs w-5 h-5 rounded-full flex items-center justify-center" aria-hidden="true">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>

      {/* Row 2: Desktop nav */}
      <div
        className={`hidden md:flex justify-center space-x-12 py-5 text-lg font-semibold font-playfair ${
          isHome ? "text-[#f5f3ea]" : "text-stone-800"
        }`}
      >
        {navLinks.map(({ path, label }) => (
          <NavLink
            key={path}
            to={path}
            className={({ isActive }) =>
              `hover:text-[#BC3F4A] transition duration-300 ${
                isActive ? "text-[#BC3F4A] underline underline-offset-4" : ""
              }`
            }
          >
            {label}
          </NavLink>
        ))}
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="fixed inset-0 bg-black z-60"
              onClick={closeMenu}
              aria-hidden="true"
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
              drag="x"
              dragDirectionLock
              dragConstraints={{ left: 0, right: 0 }}
              onDragEnd={(e, info) => {
                if (info.offset.x > 100 || info.velocity.x > 500)
                  setMenuOpen(false);
              }}
              className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-[#f5f3ea] z-70 flex flex-col space-y-2"
              role="dialog"
              aria-modal="true"
              aria-label="Mobile menu"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#e2e1d8]">
                <Link to="/" onClick={closeMenu} aria-label="Go to homepage">
                  <h2 className="text-[#BC3F4A] text-2xl font-medium tracking-tight font-playfair">
                    sanguine accent
                  </h2>
                </Link>
                <button
                  onClick={closeMenu}
                  aria-label="Close menu"
                  className="text-stone-700 hover:text-[#BC3F4A] transition"
                >
                  <X className="h-7 w-7" aria-hidden="true" />
                </button>
              </div>

              <div className="flex flex-col space-y-1 font-sourcesans text-stone-800 px-6 py-6">
                {navLinks.map(({ path, label }) => (
                  <Link
                    key={path}
                    to={path}
                    onClick={closeMenu}
                    className="hover:text-[#BC3F4A] transition text-lg"
                  >
                    {label}
                  </Link>
                ))}
              </div>

              <div className="flex flex-col space-y-1 font-sourcesans text-stone-800 px-6 py-6">
                <UserMenu
                  user={user}
                  menuOpen={menuOpen}
                  setMenuOpen={setMenuOpen}
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bottom Tab Bar — mobile only */}
      <nav
        aria-label="Bottom navigation"
        className="fixed bottom-0 left-0 right-0 bg-[#f5f3ea] border-t border-[#e2e1d8] z-30 md:hidden flex"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex justify-around w-full">
          <Link
            to="/"
            className="flex flex-col items-center justify-center py-2 hover:text-[#BC3F4A] transition"
            aria-label="Home"
          >
            <Home strokeWidth={1.5} className="w-6 h-6" aria-hidden="true" />
            <span className="text-xs mt-1">Home</span>
          </Link>

          <Link
            to="/wishlist"
            className="flex flex-col items-center justify-center py-2 hover:text-[#BC3F4A] transition"
            aria-label="Favorites"
          >
            <Bookmark strokeWidth={1.5} className="w-6 h-6" aria-hidden="true" />
            <span className="text-xs mt-1">Favorites</span>
          </Link>

          <div className="flex flex-col items-center justify-center py-2">
            <SearchPopup context="mobile" />
            <span className="text-xs mt-1">Search</span>
          </div>

          <Link
            to="/cart"
            className="flex flex-col items-center justify-center py-2 relative hover:text-[#BC3F4A] transition"
            aria-label={`Cart${cartCount > 0 ? `, ${cartCount} items` : ""}`}
          >
            <ShoppingBag strokeWidth={1.5} className="w-6 h-6" aria-hidden="true" />
            {cartCount > 0 && (
              <span className="absolute top-1 right-0 bg-[#BC3F4A] text-[#f5f3ea] text-xs w-5 h-5 rounded-full flex items-center justify-center" aria-hidden="true">
                {cartCount}
              </span>
            )}
            <span className="text-xs mt-1">Cart</span>
          </Link>

          <Link
            to={user ? "/me/profile" : "/login"}
            className="flex flex-col items-center justify-center py-2 hover:text-[#BC3F4A] transition"
            aria-label={user ? "Profile" : "Login"}
          >
            <User strokeWidth={1.5} className="w-6 h-6" aria-hidden="true" />
            <span className="text-xs mt-1">{user ? "Profile" : "Login"}</span>
          </Link>
        </div>
      </nav>
    </nav>
  );
};

export default Header;